import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { SolicitudVoleoDetail } from "@/services/voleo-requests.service";
import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import { Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import React from "react";

export const rowBg = (index: number) =>
  index % 2 === 0 ? AppColors.BACKGROUND_SECONDARY : "transparent";

export interface VoleoRow {
  label: string;
  value: React.ReactNode;
}

interface UseVoleoRequestRowsParams {
  detail: SolicitudVoleoDetail | null;
  showExecutionInfo: boolean;
}

const formatNumber = (value: unknown, suffix?: string) =>
  Number.isFinite(Number(value))
    ? `${new Intl.NumberFormat("es-AR").format(Number(value))}${suffix ? ` ${suffix}` : ""}`
    : "-";

export const useVoleoRequestRows = ({
  detail,
  showExecutionInfo,
}: UseVoleoRequestRowsParams): { leftRows: VoleoRow[]; rightRows: VoleoRow[] } => {
  const nombre = detail?.titularCuenta?.split(" - ")?.[0] ?? "-";

  const leftRows: VoleoRow[] = [
    { label: "Cuenta Productor:", value: detail?.cuentaOrigen ?? "-" },
    { label: "Nombre:", value: nombre },
    { label: "Sucursal:", value: detail?.sucursal ?? "-" },
    { label: "Establecimiento:", value: detail?.establecimiento ?? "-" },
    { label: "Tipo de servicio:", value: detail?.tipoServicio ?? "-" },
    { label: "Modalidad:", value: detail?.modalidad ?? "-" },
    { label: "Cultivo:", value: detail?.tipoCultivo ?? "-" },
    { label: "Producto:", value: detail?.producto ?? "-" },
    { label: "Dosis:", value: formatNumber(detail?.dosisKgHa, "kg/ha") },
  ];

  const rightRows: VoleoRow[] = [
    { label: "Hectáreas:", value: formatNumber(detail?.hectareas) },
    {
      label: "Cantidad total calculada:",
      value: formatNumber(detail?.cantidadTotalKg, "kg"),
    },
    {
      label: "Valor unitario:",
      value: `U$S ${formatCurrency(detail?.valorUnitario ?? 0)}/ha`,
    },
    {
      label: "Valor total:",
      value: `U$S ${formatCurrency(detail?.valorTotal ?? 0)}`,
    },
    { label: "Teléfono de contacto:", value: detail?.telefonoContacto ?? "-" },
    {
      label: "Fecha deseada:",
      value: detail?.fechaDeseada
        ? dayjs(detail.fechaDeseada).format("DD/MM/YYYY")
        : "-",
    },
    {
      label: "Estado:",
      value: (
        <Text as="span" className="account-value-label">
          <RequestStatusBadge status={detail?.estado as unknown as ServiceRequestStatus} />
        </Text>
      ),
    },
    ...(showExecutionInfo
      ? [
          {
            label: "Hectáreas ejecutadas:",
            value: formatNumber(detail?.hectareasEjecutadas),
          },
          {
            label: "Fecha real de realización:",
            value: detail?.fechaRealRealizacion
              ? dayjs(detail.fechaRealRealizacion).format("DD/MM/YYYY")
              : "-",
          },
        ]
      : []),
  ];

  return { leftRows, rightRows };
};
